import { shallowCallback, type StateCreatorEnhance } from '@halo/zustand-enhance';
import type { GroupDisallowSendInfo, GroupMyRole, GroupOptions } from '@halo/chat-sdk';
import {
  GroupJoinsOperationType,
  GroupMemberJoinMode,
  GroupMemberRole,
  GroupOptionBodyEnum,
  GroupOptionDisableEnum,
  type GroupInfo,
  type User,
} from '@halo/chat-sdk';
import type { SessionStore } from './main';

export interface GroupAccess {
  canAtAll: boolean;
  canShowMembers: boolean;
  canEditGroupInfo: boolean;
  canMediaCall: boolean;
  canSendMessage: boolean;
  canInviteMember: boolean;
  canPrivateMessage: boolean;
  canRestrictDownload: boolean;
  canRestrictForward: boolean;
  canRestrictCopying: boolean;
  canRestrictScreenshot: boolean;
  canRestrictScreenRecording: boolean;
}

export interface GroupStore {
  /**
   * current logged in user
   */
  user: User;
  groupMyRole: GroupMyRole;
  isGroupOwner: boolean;
  isGroupAdmin: boolean;
  groupAccess: GroupAccess;
  groupInfo: GroupInfo | null;
  groupOptions: GroupOptions | null;
  groupJoins: string[];
  groupDisallowSendInfo: GroupDisallowSendInfo | null;
  setGroupJoins: (groupJoins: string[]) => void;
  fetchGroupJoins: () => Promise<void>;
  setGroupInfo: (groupInfo: Partial<GroupInfo>) => void;
  setGroupOptions: (groupOptions: GroupOptions) => void;
  fetchGroupInfo: () => Promise<void>;
  getShareUrl: () => Promise<void>;
  fetchGroupOptions: () => Promise<void>;
  fetchDisallowSendInfo: () => Promise<void>;
  getMyGroupRole: () => Promise<void>;
  setMyGroupRole: (groupMyRole: Partial<GroupMyRole>) => void;
  createGroup: (options: CreateGroupOptions) => void;
  initGroup: () => Promise<void>;
  watchGroup: () => () => void;
}

export type CreateGroupOptions = Pick<GroupStore, 'user'>;

export const groupStoreCreator: StateCreatorEnhance<SessionStore> = (set, get, instance) =>
  ({
    isGroupOwner: false,
    isGroupAdmin: false,
    groupMyRole: {
      role: undefined,
      join_mode: GroupMemberJoinMode.Invite,
      invite_uid: undefined,
    },
    groupAccess: {
      canAtAll: true,
      canShowMembers: true,
      canEditGroupInfo: true,
      canMediaCall: true,
      canSendMessage: true,
      canInviteMember: true,
      canPrivateMessage: true,
      canRestrictDownload: true,
      canRestrictForward: true,
      canRestrictCopying: true,
      canRestrictScreenshot: true,
      canRestrictScreenRecording: true,
    },
    groupInfo: null,
    groupOptions: null,
    groupJoins: [],
    groupDisallowSendInfo: null,
    setGroupInfo: (groupInfo) => {
      const currentGroupInfo = get().groupInfo;
      set({ groupInfo: { ...currentGroupInfo, ...groupInfo } });
    },
    fetchGroupInfo: async () => {
      const { client, session, setGroupInfo } = get();
      const groupInfo = await client.Api.session.getGroupInfo(session.chat_id);
      setGroupInfo(groupInfo);
    },
    getShareUrl: async () => {
      const { client, session, setGroupInfo } = get();
      const { url: shareUrl } = await client.Api.http.getShareUrl({ group_id: session.chat_id });
      setGroupInfo({ share_url: shareUrl });
    },
    setGroupOptions: (groupOptions: GroupOptions) => {
      const currentGroupOptions = get().groupOptions;
      set({ groupOptions: { ...currentGroupOptions, ...groupOptions } });
    },
    fetchGroupOptions: async () => {
      const { client, session, groupOptions, setGroupOptions } = get();
      const result = await client.Api.session.getGroupOptions(session.chat_id);
      const newGroupOptions = { ...groupOptions };
      result.forEach((item) => {
        newGroupOptions[item.key] = {
          val: item.val,
          category: item.category,
        } as GroupOptions[keyof GroupOptions];
      });
      setGroupOptions({ group_id: session.chat_id, ...newGroupOptions });
    },
    setGroupJoins: (groupJoins: string[]) => {
      set({ groupJoins });
    },
    fetchGroupJoins: async () => {
      const { client, session, setGroupJoins } = get();
      const groupJoins = await client.Api.session.getGroupJoins(session.chat_id);
      setGroupJoins(groupJoins);
    },
    fetchDisallowSendInfo: async () => {
      const { client, session } = get();
      const groupDisallowSendInfo = await client.Api.session.getDisallowSendInfo(session.chat_id);
      set({ groupDisallowSendInfo });
    },
    getMyGroupRole: async () => {
      const { client, session, setMyGroupRole } = get();
      const result = await client.Api.session.getMyGroupRole(session.chat_id);
      setMyGroupRole(result);
    },
    setMyGroupRole: (groupMyRole) => {
      set((store) => {
        if (groupMyRole.role !== undefined) {
          store.isGroupOwner = groupMyRole.role === GroupMemberRole.OWNER;
          store.isGroupAdmin =
            groupMyRole.role === GroupMemberRole.OWNER ||
            groupMyRole.role === GroupMemberRole.ADMIN;
          store.groupMyRole.role = groupMyRole.role;
        }
        if (groupMyRole.join_mode !== undefined) {
          store.groupMyRole.join_mode = groupMyRole.join_mode;
        }
        if (groupMyRole.invite_uid !== undefined) {
          store.groupMyRole.invite_uid = groupMyRole.invite_uid;
        }
      });
    },
    createGroup: ({ user }: CreateGroupOptions) => {
      set({ user });

      const { client, session, watchGroup, effects } = get();

      watchGroup();

      effects.push(
        client.addEventListener('GroupMembersChanged', async () => {
          const { fetchGroupInfo } = get();
          fetchGroupInfo();
        }),
        client.addEventListener('GroupInfoChanged', ({ detail: { group_id, data } }) => {
          if (group_id === session.chat_id) {
            const { setGroupInfo } = get();
            setGroupInfo(data);
          }
        }),
        client.addEventListener('GroupNameChanged', ({ detail: { group_id, name } }) => {
          if (group_id === session.chat_id) {
            const { setGroupInfo } = get();
            setGroupInfo({ group_name: name });
          }
        }),
        client.addEventListener(
          'GroupDescriptionChanged',
          ({ detail: { group_id, description } }) => {
            if (group_id === session.chat_id) {
              const { setGroupInfo } = get();
              setGroupInfo({ description });
            }
          }
        ),
        client.addEventListener('GroupStatusChanged', ({ detail: { group_id, status } }) => {
          if (group_id === session.chat_id) {
            const { setGroupInfo } = get();
            setGroupInfo({ status });
          }
        }),
        client.addEventListener('GroupOptionsChanged', ({ detail: { group_id, data } }) => {
          if (group_id === session.chat_id) {
            const { setGroupOptions, groupOptions } = get();
            const newGroupOptions = { ...groupOptions };
            data.forEach((item) => {
              newGroupOptions[item.key] = {
                val: item.val,
                category: item.category,
              } as GroupOptions[keyof GroupOptions];
            });
            setGroupOptions(newGroupOptions);
          }
        }),
        client.addEventListener('GroupOwnerChanged', ({ detail: { group_id } }) => {
          if (group_id === session.chat_id) {
            const { getMyGroupRole } = get();
            getMyGroupRole();
          }
        }),
        client.addEventListener('GroupAdminChanged', ({ detail: { group_id } }) => {
          if (group_id === session.chat_id) {
            const { getMyGroupRole } = get();
            getMyGroupRole();
          }
        }),
        client.addEventListener(
          'GroupJoinsChanged',
          ({ detail: { group_id, operation_type, user_id_list } }) => {
            if (group_id === session.chat_id) {
              const { groupJoins, setGroupJoins } = get();
              if (operation_type === GroupJoinsOperationType.Pending) {
                setGroupJoins([...groupJoins, ...user_id_list]);
              } else {
                setGroupJoins(groupJoins.filter((user_id) => !user_id_list.includes(user_id)));
              }
            }
          }
        ),
        client.addEventListener(
          'GroupOptionsPartialChanged',
          ({ detail: { group_id, data } }) => {
            if (group_id === session.chat_id) {
              const { groupOptions, setGroupOptions } = get();
              const newGroupOptions = { ...groupOptions };
              data.forEach((val, key) => {
                newGroupOptions[key].val = val;
              });
              setGroupOptions(newGroupOptions);
            }
          }
        ),
        client.addEventListener('MyGroupRoleChanged', ({ detail: { group_id, role } }) => {
          if (group_id === session.chat_id) {
            const { fetchGroupInfo, setMyGroupRole } = get();
            setMyGroupRole({ role });
            fetchGroupInfo();
          }
        })
      );
    },
    initGroup: async () => {
      const {
        fetchGroupInfo,
        getMyGroupRole,
        getShareUrl,
        fetchGroupOptions,
        fetchGroupJoins,
        fetchDisallowSendInfo,
      } = get();
      await Promise.allSettled([
        fetchGroupInfo(),
        getMyGroupRole(),
        getShareUrl(),
        fetchGroupOptions(),
        fetchGroupJoins(),
        fetchDisallowSendInfo(),
      ]);
    },
    watchGroup: () => {
      instance.subscribe(
        (store) => [store.user, store.groupOptions, store.isGroupAdmin] as const,
        shallowCallback(([_user, groupOptions, isGroupAdmin]) => {
          const isEnable = (option?: GroupOptionDisableEnum) => {
            return option !== undefined && option !== GroupOptionDisableEnum.Disable;
          };
          const hasAccess = (option?: GroupOptionBodyEnum) => {
            return option === GroupOptionBodyEnum.Admin
              ? isGroupAdmin
              : option === GroupOptionBodyEnum.Everyone
                ? true
                : false;
          };

          const groupAccess: GroupAccess = {
            canAtAll: hasAccess(groupOptions?.at_all?.val),
            canShowMembers: isEnable(groupOptions?.hide_member?.val) ? isGroupAdmin : true,
            canEditGroupInfo: hasAccess(groupOptions?.info_edit?.val),
            canMediaCall: hasAccess(groupOptions?.media_call?.val),
            canSendMessage: hasAccess(groupOptions?.send_message?.val),
            canInviteMember: isEnable(groupOptions?.enter_audit?.val) ? isGroupAdmin : true,
            canPrivateMessage: hasAccess(groupOptions?.private_message?.val),
            canRestrictCopying: !isEnable(groupOptions?.restrict_coping?.val),
            canRestrictDownload: !isEnable(groupOptions?.restrict_download?.val),
            canRestrictForward: !isEnable(groupOptions?.restrict_forward?.val),
            canRestrictScreenshot: !isEnable(groupOptions?.restrict_screenshot?.val),
            canRestrictScreenRecording: !isEnable(groupOptions?.restrict_screen_recording?.val),
          };

          set({ groupAccess });
        })
      );
    },
  }) as SessionStore;
